import React, { useState } from "react";
import { Alert, Button, Divider, Form, Space, Typography } from "antd";
import { KeyOutlined } from "@ant-design/icons";
import { fetchPasskeyStepUpOptions, stepUpWithPasskey, stepUpWithTotp } from "../../../api/auth/mfaApi";
import { applyStepUp, loadAuthSession } from "../../../lib/http/session";
import { authFailure, authFailureMessage } from "../../../lib/http/authErrors";
import { getPasskeyAssertion, isPasskeySupported, PasskeyCeremonyError, passkeyFailureMessage } from "../../../lib/webauthn/webauthn";
import type { StepUpResponse, StrongMethod } from "../../../types/auth/mfa";
import OtpInput from "./OtpInput";
import { isOtp, normaliseOtp } from "./codes";

const { Text } = Typography;

export interface StepUpFormProps {
	methods: StrongMethod[];
	onSuccess: () => void;
	onCancel?: () => void;
	intro?: React.ReactNode;
	submitLabel?: string;
	cancelLabel?: string;
}

const stepUpError = (error: unknown, method: StrongMethod): string => {
	if (error instanceof PasskeyCeremonyError) return passkeyFailureMessage(error.reason);
	const failure = authFailure(error);
	if (failure.code === "invalid_credentials") {
		return method === "totp"
			? "That code is incorrect or was already used. Wait for the next code in your app and try again."
			: "That passkey is not registered to your account.";
	}
	if (failure.code === "challenge_expired") return "The passkey check expired. Try again.";
	if (failure.code === "mfa_enrolment_required") return "Set up an authenticator app or a passkey first.";
	return authFailureMessage(error);
};

// POST /auth/stepup with a strong factor (contract §6.7); the session store then re-reads amr, auth_time and the MFA state.
const StepUpForm: React.FC<StepUpFormProps> = ({ methods, onSuccess, onCancel, intro, submitLabel = "Confirm", cancelLabel = "Cancel" }) => {
	const [form] = Form.useForm<{ code: string }>();
	const [busy, setBusy] = useState<StrongMethod | null>(null);
	const [error, setError] = useState("");
	const offersTotp = methods.includes("totp");
	const offersPasskey = methods.includes("passkey") && isPasskeySupported();

	const finish = async (body: StepUpResponse) => {
		applyStepUp(body);
		try {
			await loadAuthSession();
		} catch {
			// Logged in loadAuthSession; the step-up itself succeeded.
		}
		onSuccess();
	};

	const submitTotp = async ({ code }: { code: string }) => {
		setBusy("totp");
		setError("");
		try {
			await finish(await stepUpWithTotp(normaliseOtp(code)));
		} catch (err) {
			setError(stepUpError(err, "totp"));
			form.setFieldValue("code", "");
		} finally {
			setBusy(null);
		}
	};

	const confirmWithPasskey = async () => {
		setBusy("passkey");
		setError("");
		try {
			const { publicKey, flow_id } = await fetchPasskeyStepUpOptions();
			const credential = await getPasskeyAssertion(publicKey);
			await finish(await stepUpWithPasskey(flow_id, credential));
		} catch (err) {
			setError(stepUpError(err, "passkey"));
		} finally {
			setBusy(null);
		}
	};

	return (
		<div data-testid="stepup-form">
			{intro && <div style={{ marginBottom: "var(--mg-sp-6)" }}>{intro}</div>}
			{error && <Alert data-testid="stepup-error" type="error" showIcon message={error} style={{ marginBottom: "var(--mg-sp-6)" }} />}
			{offersTotp && (
				<Form form={form} name="stepup" layout="vertical" onFinish={submitTotp} requiredMark={false}>
					<Form.Item
						name="code"
						label="Code from your authenticator app"
						rules={[{ validator: (_, value) => (isOtp(value ?? "") ? Promise.resolve() : Promise.reject(new Error("Enter the 6-digit code"))) }]}
					>
						<OtpInput data-testid="stepup-code" autoFocus disabled={busy !== null} />
					</Form.Item>
					<Space wrap>
						<Button data-testid="stepup-submit" type="primary" htmlType="submit" loading={busy === "totp"} disabled={busy === "passkey"}>
							{submitLabel}
						</Button>
						{onCancel && !offersPasskey && (
							<Button data-testid="stepup-cancel" onClick={onCancel} disabled={busy !== null}>
								{cancelLabel}
							</Button>
						)}
					</Space>
				</Form>
			)}
			{offersTotp && offersPasskey && <Divider plain>or</Divider>}
			{offersPasskey && (
				<Space wrap>
					<Button data-testid="stepup-passkey" icon={<KeyOutlined />} onClick={confirmWithPasskey} loading={busy === "passkey"} disabled={busy === "totp"} type={offersTotp ? "default" : "primary"}>
						Use a passkey
					</Button>
					{onCancel && (
						<Button data-testid="stepup-cancel" onClick={onCancel} disabled={busy !== null}>
							{cancelLabel}
						</Button>
					)}
				</Space>
			)}
			{!offersTotp && !offersPasskey && (
				<Space direction="vertical">
					<Text type="secondary">This browser cannot use your passkey. Try another device or browser.</Text>
					{onCancel && (
						<Button data-testid="stepup-cancel" onClick={onCancel}>
							{cancelLabel}
						</Button>
					)}
				</Space>
			)}
		</div>
	);
};

export default StepUpForm;
